import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode
} from 'react';

export interface ThemeColors {
  primary: string;
  primaryDark: string;
  primaryLight: string;
  secondary: string;
  accent: string;
  accentLight: string;
  bodyBg: string;
  sidebarBg: string;
  sidebarHover: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  border: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  colors: ThemeColors;
}

interface ThemeContextValue {
  themeColors: ThemeColors;
  activePreset: string;
  schoolLogo: string | null;
  logoFileName: string;
  applyPreset: (presetId: string) => void;
  updateColor: (key: keyof ThemeColors, value: string) => void;
  updateLogo: (base64: string | null, fileName?: string) => void;
  removeLogo: () => void;
  resetToDefault: () => void;
  presets: ThemePreset[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
};

export const PRESET_THEMES: ThemePreset[] = [
  {
    id: 'golden-pastel',
    name: 'Golden Pastel',
    description: 'Warm gold & pink — the default EduManage look',
    colors: {
      primary: '#d4a053', primaryDark: '#b8860b', primaryLight: '#f0d68a',
      secondary: '#f472b6', accent: '#f472b6', accentLight: '#fbcfe8',
      bodyBg: '#fefcf8', sidebarBg: '#fffaf3', sidebarHover: '#fef3e2',
      cardBg: '#ffffff', textPrimary: '#3d2c1e', textSecondary: '#78716c',
      textMuted: '#a8a29e', border: '#f5e6d3'
    }
  },
  {
    id: 'royal-blue',
    name: 'Royal Blue',
    description: 'Classic academic blue — professional & trusted',
    colors: {
      primary: '#3b82f6', primaryDark: '#1d4ed8', primaryLight: '#93c5fd',
      secondary: '#8b5cf6', accent: '#8b5cf6', accentLight: '#ddd6fe',
      bodyBg: '#f8fafc', sidebarBg: '#f0f4ff', sidebarHover: '#dbeafe',
      cardBg: '#ffffff', textPrimary: '#1e293b', textSecondary: '#64748b',
      textMuted: '#94a3b8', border: '#e2e8f0'
    }
  },
  {
    id: 'forest-green',
    name: 'Forest Green',
    description: 'Nature-inspired green — calm & refreshing',
    colors: {
      primary: '#16a34a', primaryDark: '#15803d', primaryLight: '#86efac',
      secondary: '#f59e0b', accent: '#f59e0b', accentLight: '#fef3c7',
      bodyBg: '#f8fdf8', sidebarBg: '#f0fdf4', sidebarHover: '#dcfce7',
      cardBg: '#ffffff', textPrimary: '#14532d', textSecondary: '#4b5563',
      textMuted: '#9ca3af', border: '#d1fae5'
    }
  },
  {
    id: 'classic-maroon',
    name: 'Classic Maroon',
    description: 'Deep maroon & gold — traditional school feel',
    colors: {
      primary: '#9f1239', primaryDark: '#881337', primaryLight: '#fda4af',
      secondary: '#d97706', accent: '#d97706', accentLight: '#fef3c7',
      bodyBg: '#fdf8f8', sidebarBg: '#fef2f2', sidebarHover: '#fecaca',
      cardBg: '#ffffff', textPrimary: '#450a0a', textSecondary: '#6b7280',
      textMuted: '#9ca3af', border: '#fecdd3'
    }
  },
  {
    id: 'ocean-teal',
    name: 'Ocean Teal',
    description: 'Serene teal & coral — modern & clean',
    colors: {
      primary: '#0d9488', primaryDark: '#0f766e', primaryLight: '#99f6e4',
      secondary: '#f472b6', accent: '#f472b6', accentLight: '#fbcfe8',
      bodyBg: '#f0fdfa', sidebarBg: '#f0fdfa', sidebarHover: '#ccfbf1',
      cardBg: '#ffffff', textPrimary: '#134e4a', textSecondary: '#64748b',
      textMuted: '#94a3b8', border: '#ccfbf1'
    }
  },
  {
    id: 'purple-reign',
    name: 'Purple Reign',
    description: 'Elegant purple — creative & inspiring',
    colors: {
      primary: '#7c3aed', primaryDark: '#6d28d9', primaryLight: '#c4b5fd',
      secondary: '#ec4899', accent: '#ec4899', accentLight: '#fbcfe8',
      bodyBg: '#faf5ff', sidebarBg: '#faf5ff', sidebarHover: '#ede9fe',
      cardBg: '#ffffff', textPrimary: '#3b0764', textSecondary: '#6b7280',
      textMuted: '#9ca3af', border: '#e9d5ff'
    }
  },
  {
    id: 'sunset-orange',
    name: 'Sunset Orange',
    description: 'Vibrant orange & blue — energetic & warm',
    colors: {
      primary: '#ea580c', primaryDark: '#c2410c', primaryLight: '#fdba74',
      secondary: '#2563eb', accent: '#2563eb', accentLight: '#dbeafe',
      bodyBg: '#fffbf5', sidebarBg: '#fff7ed', sidebarHover: '#ffedd5',
      cardBg: '#ffffff', textPrimary: '#431407', textSecondary: '#6b7280',
      textMuted: '#9ca3af', border: '#fed7aa'
    }
  },
  {
    id: 'slate-modern',
    name: 'Slate Modern',
    description: 'Sleek dark slate — minimal & professional',
    colors: {
      primary: '#475569', primaryDark: '#334155', primaryLight: '#94a3b8',
      secondary: '#f59e0b', accent: '#f59e0b', accentLight: '#fef3c7',
      bodyBg: '#f8fafc', sidebarBg: '#f1f5f9', sidebarHover: '#e2e8f0',
      cardBg: '#ffffff', textPrimary: '#0f172a', textSecondary: '#475569',
      textMuted: '#94a3b8', border: '#e2e8f0'
    }
  }
];

const DEFAULT_THEME = PRESET_THEMES[0];

const buildCssVars = (colors: ThemeColors): Record<string, string> => ({
  '--primary': colors.primary,
  '--primary-dark': colors.primaryDark,
  '--primary-light': colors.primaryLight,
  '--primary-gradient': `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  '--secondary': colors.secondary,
  '--secondary-gradient': `linear-gradient(135deg, ${colors.accentLight} 0%, ${colors.secondary} 100%)`,
  '--accent': colors.accent,
  '--accent-light': colors.accentLight,
  '--body-bg': colors.bodyBg,
  '--sidebar-bg': colors.sidebarBg,
  '--sidebar-hover': colors.sidebarHover,
  '--sidebar-active': `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
  '--card-bg': colors.cardBg,
  '--text-primary': colors.textPrimary,
  '--text-secondary': colors.textSecondary,
  '--text-muted': colors.textMuted,
  '--border': colors.border
});

const applyCssVars = (vars: Record<string, string>): void => {
  const root = document.documentElement;
  Object.entries(vars).forEach(([key, value]) => root.style.setProperty(key, value));
};

const saveTheme = (
  colors: ThemeColors,
  presetId: string,
  logo: string | null,
  logoFileName: string
): void => {
  try {
    localStorage.setItem('schoolTheme', JSON.stringify({ colors, presetId, logo, logoFileName }));
  } catch (e) {
    console.error('Failed to save theme', e);
  }
};

interface ProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ProviderProps> = ({ children }) => {
  const [themeColors, setThemeColors] = useState<ThemeColors>(DEFAULT_THEME.colors);
  const [activePreset, setActivePreset] = useState<string>(DEFAULT_THEME.id);
  const [schoolLogo, setSchoolLogo] = useState<string | null>(null);
  const [logoFileName, setLogoFileName] = useState<string>('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem('schoolTheme');
      if (saved) {
        const parsed = JSON.parse(saved) as {
          colors?: ThemeColors;
          presetId?: string;
          logo?: string;
          logoFileName?: string;
        };
        if (parsed.colors) setThemeColors(parsed.colors);
        if (parsed.presetId) setActivePreset(parsed.presetId);
        if (parsed.logo) setSchoolLogo(parsed.logo);
        if (parsed.logoFileName) setLogoFileName(parsed.logoFileName);
      }
    } catch (e) {
      console.error('Failed to load saved theme', e);
    }
  }, []);

  useEffect(() => {
    applyCssVars(buildCssVars(themeColors));
  }, [themeColors]);

  const applyPreset = useCallback((presetId: string) => {
    const preset = PRESET_THEMES.find(p => p.id === presetId);
    if (preset) {
      setThemeColors(preset.colors);
      setActivePreset(presetId);
      saveTheme(preset.colors, presetId, schoolLogo, logoFileName);
    }
  }, [schoolLogo, logoFileName]);

  const updateColor = useCallback((key: keyof ThemeColors, value: string) => {
    setThemeColors(prev => {
      const updated = { ...prev, [key]: value };
      setActivePreset('custom');
      saveTheme(updated, 'custom', schoolLogo, logoFileName);
      return updated;
    });
  }, [schoolLogo, logoFileName]);

  const updateLogo = useCallback((base64: string | null, fileName?: string) => {
    setSchoolLogo(base64);
    setLogoFileName(fileName || '');
    saveTheme(themeColors, activePreset, base64, fileName || '');
  }, [themeColors, activePreset]);

  const removeLogo = useCallback(() => {
    setSchoolLogo(null);
    setLogoFileName('');
    saveTheme(themeColors, activePreset, null, '');
  }, [themeColors, activePreset]);

  const resetToDefault = useCallback(() => {
    setThemeColors(DEFAULT_THEME.colors);
    setActivePreset(DEFAULT_THEME.id);
    setSchoolLogo(null);
    setLogoFileName('');
    saveTheme(DEFAULT_THEME.colors, DEFAULT_THEME.id, null, '');
  }, []);

  return (
    <ThemeContext.Provider value={{
      themeColors,
      activePreset,
      schoolLogo,
      logoFileName,
      applyPreset,
      updateColor,
      updateLogo,
      removeLogo,
      resetToDefault,
      presets: PRESET_THEMES
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
